import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StyledActionButton } from "../../components/StyledActionButton";
import { StyledSearchInput } from "../../components/StyledFilterInput";
import { StyledModalItem } from "../../components/StyledModalItem";
import { StyledSectionHeader } from "../../components/StyledSectionHeader";
import { StyledSelect } from "../../components/StyledSelect";
import TablePageFooter, { DefaultPagination } from "../../components/TablePageFooter";
import AMLList from "../InspectList";
import UserAutocomplete, { UserSuggestion } from "../../components/UserAutoComplete";
import CryptoPrivateAssetAutoComplete, {
    CryptoSuggestion,
} from "../../components/CryptoPrivatAssetAutoComplete";
import {
    ISorting,
    IAml,
    WindowType,
    SortingField,
    IFilter,
    selectAmlRecords,
    selectAmlRecordRelated,
    fetchAmlRecords,
    complianceAmlRecord,
    flagAmlRecord,
    fetchAmlRelated,
    reviewAmlRecord,
    AmlType,
    IRelativeSorting,
    RelativeSortingField,
} from "../AnalyticsSlice";
import { FilterField, filterLabel, IFilterDisplay } from "../AnalyticFilter";
import { SecurityAutoComplete, SecuritySuggestion } from "../../components/SecurityAutoComplete";
import RelatedTxList from "../RelatedTxList";
import ComplianceModal from "../ComplianceModal";
import { IPagination } from "../../util/pagination";

function AML(): JSX.Element {
    const dispatch = useDispatch();
    const records = useSelector(selectAmlRecords);
    const related = useSelector(selectAmlRecordRelated);

    const requestRecords = () => {
        dispatch(
            fetchAmlRecords({
                ...pagination,
                filter: {
                    ...filterMultiple,
                },
                sorting: [sorting],
            })
        );
    };

    const complianceRecord = (r: IAml, c: string) => {
        const p = new Promise((resolve) => {
            dispatch(
                complianceAmlRecord({
                    record: r,
                    comment: c,
                })
            );
            resolve({});
        });
        p.then(() => {
            requestRecords();
        });
    };

    const flagRecord = (record: IAml) => {
        const p = new Promise((resolve) => {
            dispatch(flagAmlRecord({ record }));
            resolve({});
        });
        p.then(() => {
            requestRecords();
        });
    };

    const reviewRecord = (record: IAml) => {
        const p = new Promise((resolve) => {
            dispatch(reviewAmlRecord({ record }));
            resolve({});
        });
        p.then(() => {
            requestRecords();
        });
    };

    const requestRelated = (record: IAml) => {
        setActiveSuspect(record);
        dispatch(
            fetchAmlRelated({
                record,
                sorting: [relativeSorting],
                ...paginationRelated,
            })
        );
    };

    /**
     * Analytics state
     */

    const defaultFilters: IFilter = {};
    const defaultFilterDisplays: IFilterDisplay = {};

    const company = useSelector((state: any) => state.auth.companyName);

    const [pagination, setPagination] = useState<IPagination>(DefaultPagination());
    const [filterMultiple, setFilterMultiple] = useState<IFilter>(defaultFilters);
    const [filterMultipleDisplay, setFilterMultipleDisplay] =
        useState<IFilterDisplay>(defaultFilterDisplays);
    const [filter, setFilter] = useState<FilterField | "dayRange">("userId");
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState<string>();
    const [endDate, setEndDate] = useState<string>();
    const [cachedOption, setCachedOption] = useState<string>("");
    const [searchAssetType, setSearchAssetType] = useState<"security" | "crypto">("security");

    const [paginationRelated, setPaginationRelated] = useState<IPagination>(DefaultPagination());
    const [activeSuspect, setActiveSuspect] = useState<IAml>();
    const [compliance, setCompliance] = useState<IAml>();

    const [sorting, setSorting] = useState<ISorting>({
        field: "numTrades",
        direction: "asc",
    });
    const [relativeSorting, setRelativeSorting] = useState<IRelativeSorting>({
        field: "date",
        direction: "desc",
    });

    const version = useSelector((state: any) => state.auth.version);

    const resetFilter = () => {
        setFilterMultiple(defaultFilters);
        setFilterMultipleDisplay(defaultFilterDisplays);
    };

    const filterOptions = useMemo(() => {
        return (
            <>
                <option value="asset">Asset</option>
                <option value="userId">User</option>
                <option value="dayRange">Range</option>
                <option value="windowType">Windows</option>
                <option value="type">Type</option>
                <option value="flagged">Flag</option>
            </>
        );
    }, [company, version]);

    const setFilterType = (filterType: FilterField) => {
        setSearch("");
        setStartDate("");
        setEndDate("");
        setCachedOption("");
        setFilter(filterType);
    };

    const removeFilter = (filterName: string) => {
        if (filterName in filterMultiple) {
            delete (filterMultiple as any)[filterName];
        }
        if (filterName in filterMultipleDisplay) {
            delete (filterMultipleDisplay as any)[filterName];
        }

        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
    };

    const setUserId = (user: UserSuggestion | undefined) => {
        if (user !== undefined) {
            filterMultiple.userId = user.id;
            filterMultipleDisplay.userId = user.name;
        } else {
            delete filterMultiple.userId;
            delete filterMultipleDisplay.userId;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
    };

    const setAssetSecurity = (security: SecuritySuggestion | undefined) => {
        if (security !== undefined) {
            filterMultiple.asset = {id: security.id, type: "SECURITY"};
            filterMultipleDisplay.asset = `${security.name} - ${security.exchangeName} (${security.ticker})`;
        } else {
            delete filterMultiple.asset;
            delete filterMultipleDisplay.asset;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
    };

    const setAssetCrypto = (crypto: CryptoSuggestion | undefined) => {
        if (crypto !== undefined) {
            filterMultiple.asset = {id: crypto.id, type: "CRYPTO"};
            filterMultipleDisplay.asset = `${crypto.name} - ${crypto.platform} (${crypto.ticker})`;
        } else {
            delete filterMultiple.asset;
            delete filterMultipleDisplay.asset;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
    };

    const setWindowType = (windowType: WindowType) => {
        if (windowType) {
            filterMultiple.windowType = windowType;
            filterMultipleDisplay.windowType = windowType;
        } else {
            delete filterMultiple.windowType;
            delete filterMultipleDisplay.windowType;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
        setCachedOption("");
    };

    const setAmlType = (amlType: AmlType) => {
        if (amlType) {
            filterMultiple.type = amlType;
            filterMultipleDisplay.type = amlType;
        } else {
            delete filterMultiple.type;
            delete filterMultipleDisplay.type;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
        setCachedOption("");
    };

    const setFlagFilter = (flagged: boolean | undefined) => {
        if (flagged !== undefined) {
            filterMultiple.flagged = flagged;
            filterMultipleDisplay.flagged = flagged ? "Flagged" : "Non-flagged";
        } else {
            delete filterMultiple.flagged;
            delete filterMultipleDisplay.flagged;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
        setCachedOption("");
    };

    const setDayRange = (
        startDate: string | undefined,
        endDate: string | undefined
    ) => {
        if (startDate && endDate) {
            filterMultiple.startDate = startDate;
            filterMultiple.endDate = endDate;
            filterMultipleDisplay.range = `${startDate} - ${endDate}`;
        } else {
            delete filterMultiple.startDate;
            delete filterMultiple.endDate;
            delete filterMultipleDisplay.range;
        }
        setFilterMultiple({ ...filterMultiple });
        setFilterMultipleDisplay({ ...filterMultipleDisplay });
    };

    useEffect(() => {
        requestRecords();
    }, [dispatch, pagination, sorting]);

    useEffect(() => {
        if (paginationRelated.pageNumber == 1 && activeSuspect) {
            // directly request related
            requestRelated(activeSuspect);
        } else {
            setPaginationRelated({
                ...paginationRelated,
                pageNumber: 1
            });
        }
    }, [dispatch, activeSuspect]);

    useEffect(() => {
        if (activeSuspect) {
            requestRelated(activeSuspect);
        }
    }, [dispatch, paginationRelated, relativeSorting]);

    return (
        <div>
            <StyledSectionHeader>Filters</StyledSectionHeader>

            <div style={{ display: "flex", flexDirection: "row" }}>
                <StyledModalItem style={{ display: "flex", width: "25%" }}>
                    <StyledSelect
                        id="filterValue"
                        name="filterValue"
                        style={{ width: "100%" }}
                        value={filter}
                        onChange={(event) => {
                            setFilterType(event.target.value as FilterField);
                        }}
                    >
                        {filterOptions}
                    </StyledSelect>
                </StyledModalItem>

                <div style={{ flex: 1 }}>
                    {
                        <>
                            {filter === "userId" && (
                                <div style={{ position: "relative" }}>
                                    <UserAutocomplete
                                        value={search}
                                        onSelect={setUserId}
                                        onEnter={setUserId}
                                        style={{ width: "100%" }}
                                        placeholder={"Filter by name or email"}
                                    />
                                </div>
                            )}
                            {filter === "asset" ? (
                                <div style={{ display: "flex", flexDirection: "row" }}>
                                    <div style={{ position: "relative", width: "50%" }}>
                                        <StyledSelect
                                            id="filterValue"
                                            name="filterValue"
                                            style={{ width: "100%" }}
                                            onChange={(event) => {
                                                setSearchAssetType(
                                                    event.target.value as
                                                        | "security"
                                                        | "crypto"
                                                );
                                            }}
                                        >
                                            <option value="security">Security</option>
                                            <option value="crypto">Crypto</option>
                                        </StyledSelect>
                                    </div>
                                    <div style={{ position: "relative", width: "50%" }}>
                                        {searchAssetType === "security" ? (
                                            <SecurityAutoComplete
                                                placeholder={"Ticker or name"}
                                                value={search}
                                                style={{ width: "100%" }}
                                                onSelect={setAssetSecurity}
                                                onEnter={setAssetSecurity}
                                            />
                                        ) : (
                                            <CryptoPrivateAssetAutoComplete
                                                placeholder={"Ticker or name"}
                                                value={search}
                                                style={{ width: "100%" }}
                                                onSelect={setAssetCrypto}
                                                onEnter={setAssetCrypto}
                                            />
                                        )}
                                    </div>
                                </div>
                            ) : null}
                            {filter === "windowType" ? (
                                <>
                                    <div style={{ position: "relative" }}>
                                        <StyledSelect
                                            id="windowType"
                                            name="windowType"
                                            style={{ width: "100%" }}
                                            value={cachedOption}
                                            onChange={(event) => {
                                                setWindowType(
                                                    event.target.value as WindowType
                                                );
                                            }}
                                        >
                                            <option value="">None</option>
                                            <option value="DAILY">Daily</option>
                                            <option value="WEEK">Week</option>
                                            <option value="MONTH">Month</option>
                                        </StyledSelect>
                                    </div>
                                </>
                            ) : null}
                            {filter === "flagged" ? (
                                <>
                                    <div style={{ position: "relative" }}>
                                        <StyledSelect
                                            id="flagged"
                                            name="flagged"
                                            style={{ width: "100%" }}
                                            value={cachedOption}
                                            onChange={(event) => {
                                                switch (event.target.value) {
                                                    case "Flag":
                                                        setFlagFilter(true);
                                                        return;
                                                    case "NonFlag":
                                                        setFlagFilter(false);
                                                        return;
                                                }
                                            }}
                                        >
                                            <option value="ALL">All</option>
                                            <option value="Flag">FLAGGED</option>
                                            <option value="NonFlag">Non-FLAGGED</option>
                                        </StyledSelect>
                                    </div>
                                </>
                            ) : null}
                            {filter === "dayRange" ? (
                                <>
                                    <div
                                        style={{
                                            position: "relative",
                                            display: "flex",
                                            lineHeight: "40px",
                                        }}
                                    >
                                        <StyledSearchInput
                                            placeholder="Start Date"
                                            type={"date"}
                                            value={startDate}
                                            onChange={(event) => {
                                                setStartDate(event.target.value);
                                                setDayRange(
                                                    event.target.value,
                                                    endDate
                                                );
                                            }}
                                            style={{ display: "flex" }}
                                        />
                                        <span> - </span>
                                        <StyledSearchInput
                                            placeholder="End Date"
                                            type={"date"}
                                            value={endDate}
                                            onChange={(event) => {
                                                setEndDate(event.target.value);
                                                setDayRange(
                                                    startDate,
                                                    event.target.value
                                                );
                                            }}
                                            style={{ display: "flex" }}
                                        />
                                    </div>
                                </>
                            ) : null}
                            {filter === "type" ? (
                                <>
                                    <div style={{ position: "relative" }}>
                                        <StyledSelect
                                            id="amlType"
                                            name="amlType"
                                            style={{ width: "100%" }}
                                            value={cachedOption}
                                            onChange={(event) => {
                                                setAmlType(
                                                    event.target.value as AmlType
                                                );
                                            }}
                                        >
                                            <option value="">All</option>
                                            <option value={AmlType.HighRiskCurrency}>
                                                High Risk Currency
                                            </option>
                                            <option value={AmlType.HighValueOutgoing}>
                                                High Value Outgoing
                                            </option>
                                            <option value={AmlType.LossMakingTrading}>
                                                Loss Making Trading
                                            </option>
                                            <option value={AmlType.LowValueTrading}>
                                                Low Value Trading
                                            </option>
                                            <option value={AmlType.StructuringIn}>
                                                Structuring In
                                            </option>
                                            <option value={AmlType.StructuringOut}>
                                                Structuring Out
                                            </option>
                                        </StyledSelect>
                                    </div>
                                </>
                            ) : null}
                        </>
                    }
                </div>

                <StyledModalItem
                    style={{
                        display: "flex",
                        width: "25%",
                        flexDirection: "row",
                        gap: ".25em",
                        margin: "0 .25em",
                    }}
                >
                    <StyledActionButton style={{ flex: 1 }} onClick={resetFilter}>
                        Clear Filters
                    </StyledActionButton>
                    <StyledActionButton
                        style={{ flex: 1 }}
                        onClick={() => {
                            setPagination({
                                ...pagination,
                                pageNumber: 1,
                            });
                            requestRecords();
                        }}
                    >
                        Apply
                    </StyledActionButton>
                </StyledModalItem>
            </div>

            <div
                style={{ display: "flex", flexDirection: "row", marginBottom: "1rem" }}
            >
                {Object.keys(filterMultiple)
                    .filter((fieldName: string) => fieldName !== "endDate")
                    .map((fieldName: string, index: number) => (
                        <span key={fieldName}>
                            {(filterMultiple as any)[fieldName] !== ""
                                ? filterLabel(
                                      fieldName,
                                      filterMultipleDisplay,
                                      filterMultiple,
                                      removeFilter
                                  )
                                : null}
                        </span>
                    ))}
            </div>

            <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
                <div className="inspect-column">
                    <AMLList
                        records={records.data}
                        selected={activeSuspect}
                        sorting={sorting}
                        setSorting={(s) =>
                            setSorting({
                                field: s.field as SortingField,
                                direction: s.direction,
                            })
                        }
                        onSelect={setActiveSuspect}
                        onFlag={flagRecord}
                        onCompliance={setCompliance}
                        onReview={reviewRecord}
                    />
                    <TablePageFooter
                        nextPage={records.nextPage}
                        pagination={pagination}
                        setPageNumber={(n) =>
                            setPagination({ ...pagination, pageNumber: n })
                        }
                        setPageSize={(s) =>
                            setPagination({ ...pagination, pageSize: s })
                        }
                    />
                </div>
                <div className="inspect-column">
                    <RelatedTxList
                        txList={related.data}
                        setSorting={(s) =>
                            setRelativeSorting({
                                field: s.field as RelativeSortingField,
                                direction: s.direction,
                            })
                        }
                        sorting={relativeSorting}
                    />
                    <TablePageFooter
                        nextPage={related.nextPage}
                        pagination={paginationRelated}
                        setPageNumber={(n) =>
                            setPaginationRelated({ ...paginationRelated, pageNumber: n })
                        }
                        setPageSize={(s) =>
                            setPaginationRelated({ ...paginationRelated, pageSize: s })
                        }
                    />
                </div>
            </div>

            <ComplianceModal<IAml>
                record={compliance}
                onClose={() => setCompliance(undefined)}
                title={`Compliance AML Record [${compliance?.username}]`}
                onCompliance={complianceRecord}
            />
        </div>
    );
}

export default AML;
